import { Box, Grid, GridSize, Tooltip } from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Checkbox from "@saleor/components/Checkbox";
import CountTextField from "@saleor/components/CountTextField";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import SingleSelectField from "@saleor/components/SingleSelectField";
import TextInputDisable from "@saleor/components/TextInputDisable";
import { formatThousandDecimal } from "@saleor/utils/number";
import classNames from "classnames";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";

import DocumentUploader from "./DocumentUploader";
import useStyles from "./styles";
import { FeeTypeLabel } from "./types";
import useCashSaleForm from "./useCashSaleForm";

export type CashSaleFormProps = ReturnType<typeof useCashSaleForm>;
interface FormCell {
  size: GridSize;
  content: React.ReactNode | string;
  className?: string;
}

interface FormRow {
  label: {
    size?: GridSize;
    text: React.ReactNode | string;
    subText?: React.ReactNode | string;
  };
  cells: FormCell[];
}

const CashSaleForm: React.FC<CashSaleFormProps> = ({
  isEditable: isEditableProps = false,
  formik,
  arOutstandingResult,
  feeTypeOptions,
  handleFormMountChange,
  handleChangeField,
  handleBlurField
}) => {
  const classes = useStyles();

  const arOutstanding = arOutstandingResult?.data?.getArOutstanding;
  const isLoading = arOutstandingResult?.loading;
  const isEditable = isEditableProps && !isLoading;

  useEffect(() => {
    handleFormMountChange(true);
    return () => handleFormMountChange(false);
  }, []);

  const renderFormRow = (rowKey: string, { label, cells }: FormRow) => {
    const childrens = cells.map((cell, index) => (
      <Grid key={`${rowKey}-${index}`} item xs={cell.size}>
        <div className={cell.className}>{cell.content}</div>
      </Grid>
    ));
    return (
      <Grid key={rowKey} item container spacing={2}>
        <Grid item container xs={label.size ?? 7}>
          <div className={classes.rowText}>
            {!!label.text && (
              <MyTypography variant="subtitle3">{label.text}</MyTypography>
            )}
            {!!label.subText && (
              <MyTypography variant="body3" style={{ marginLeft: 5 }}>
                {label.subText}
              </MyTypography>
            )}
          </div>
        </Grid>
        {childrens}
      </Grid>
    );
  };

  const currencyCell: FormCell = {
    size: 1,
    content: (
      <MyTypography variant="body3" align="right">
        <FormattedMessage defaultMessage="THB" />
      </MyTypography>
    ),
    className: classes.rowText
  };

  const renderTextResult = (
    status: "success" | "error" | "pending",
    message: string
  ) => {
    if (!message) {
      return null;
    }
    const classNameMap = {
      success: classes.textSuccess,
      error: classes.textError,
      pending: classes.textPending
    };
    return (
      <div className={classNames(classes.textResult, classNameMap[status])}>
        {message}
      </div>
    );
  };

  const renderCustomerBalanceStatus = () => {
    if (isLoading) {
      return renderTextResult("pending", "กำลังโหลดข้อมูล...");
    }
    if (arOutstanding?.status) {
      return renderTextResult("success", "เชื่อมต่อข้อมูลสำเร็จ");
    }
    if (!arOutstandingResult?.data && !arOutstandingResult?.error) {
      return null;
    }
    return renderTextResult("error", "เชื่อมต่อข้อมูลไม่สำเร็จ");
  };

  const getDecimalDigits = (amount: number) => (amount === 0 ? 0 : 2);

  const rows: Array<{ key: string; config: FormRow }> = [
    {
      key: "orderAmount",
      config: {
        label: {
          text: <FormattedMessage defaultMessage="Order Amount" />,
          subText: <FormattedMessage defaultMessage="(Inc. VAT)" />
        },
        cells: [
          {
            size: 4,
            content: (
              <TextInputDisable
                value={formatThousandDecimal(
                  Number(formik?.values?.orderAmount),
                  getDecimalDigits(Number(formik?.values?.orderAmount))
                )}
                className={classNames(classes.readOnlyNumber, classes.textBold)}
                testId="orderAmount-text"
              />
            )
          },
          currencyCell
        ]
      }
    },
    {
      key: "customerBalance",
      config: {
        label: {
          text: <FormattedMessage defaultMessage="เงินหลือของลูกค้า" />,
          subText: (
            <Tooltip
              title={
                <FormattedMessage defaultMessage="จำนวนยอดเงินคงเหลือ AR Outstanding" />
              }
              placement="bottom-start"
              classes={{ popper: classes.tooltipPopper }}
              arrow
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          )
        },
        cells: [
          {
            size: 4,
            content: (
              <>
                <TextInputDisable
                  value={formatThousandDecimal(
                    Number(formik.values.arAmount),
                    getDecimalDigits(Number(formik.values.arAmount))
                  )}
                  className={classes.readOnlyNumber}
                  testId="customerBalance-text"
                />
                {renderCustomerBalanceStatus()}
              </>
            )
          },
          currencyCell
        ]
      }
    },
    {
      key: "cashAmount",
      config: {
        label: {
          text: (
            <>
              <Checkbox
                name="cashFlag"
                data-testid="cashFlag-checkbox"
                className={classes.checkbox}
                size="small"
                checked={formik.values.cashFlag}
                disabled={!isEditable}
                onChange={handleChangeField}
              />
              <FormattedMessage defaultMessage="จำนวนเงินโอน" />
            </>
          ),
          subText: <FormattedMessage defaultMessage="(Inc. VAT)" />
        },
        cells: [
          {
            size: 4,
            content: (
              <>
                {isEditable ? (
                  <NumberFormat
                    name="cashAmount"
                    testId="cashAmount-input"
                    value={`${formik.values.cashAmount}`}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                    placeholder="0"
                    decimalScale={2}
                    fixedDecimalScale
                    rightAlignPlaceholder
                    customClass={classes.numberInput}
                    inputDisabled={!formik.values.cashFlag}
                    error={"cashAmount" in formik.errors}
                  />
                ) : (
                  <TextInputDisable
                    value={formatThousandDecimal(
                      Number(formik.values.cashAmount),
                      getDecimalDigits(Number(formik.values.cashAmount))
                    )}
                    className={classes.readOnlyNumber}
                    testId="cashAmount-text"
                  />
                )}
                {isEditable &&
                  renderTextResult("error", formik.errors?.cashAmount)}
              </>
            )
          },
          currencyCell
        ]
      }
    },
    {
      key: "balanceAmount",
      config: {
        label: {
          text: (
            <>
              <Checkbox
                name="balanceFlag"
                data-testid="balanceFlag-checkbox"
                className={classes.checkbox}
                size="small"
                checked={formik.values.balanceFlag}
                disabled={!isEditable}
                onChange={handleChangeField}
              />
              <FormattedMessage defaultMessage="เงินเหลือที่ต้องการใช้" />
            </>
          ),
          subText: <FormattedMessage defaultMessage="(Inc. VAT)" />
        },
        cells: [
          {
            size: 4,
            content: (
              <>
                {isEditable ? (
                  <NumberFormat
                    name="balanceAmount"
                    testId="balanceAmount-input"
                    value={`${formik.values.balanceAmount}`}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                    placeholder="0"
                    decimalScale={2}
                    fixedDecimalScale
                    rightAlignPlaceholder
                    customClass={classes.numberInput}
                    inputDisabled={!formik.values.balanceFlag}
                    error={"balanceAmount" in formik.errors}
                  />
                ) : (
                  <TextInputDisable
                    value={formatThousandDecimal(
                      Number(formik.values.balanceAmount),
                      getDecimalDigits(Number(formik.values.balanceAmount))
                    )}
                    className={classes.readOnlyNumber}
                    testId="balanceAmount-text"
                  />
                )}
                {isEditable &&
                  renderTextResult("error", formik.errors?.balanceAmount)}
              </>
            )
          },
          currencyCell
        ]
      }
    },
    {
      key: "customerPaymentDifference",
      config: {
        label: {
          text: <FormattedMessage defaultMessage="ลูกค้าจ่ายขาด/จ่ายเกิน" />,
          subText: (
            <Tooltip
              title={
                <>
                  <FormattedMessage defaultMessage="Formula:" />
                  <br />
                  <b>[</b> (Order Amount - WHT) - (จำนวนเงินโอน +
                  จำนวนเงินเหลือที่ต้องการใช้) <b>]</b>
                </>
              }
              placement="bottom-start"
              classes={{ popper: classes.tooltipPopper }}
              arrow
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          )
        },
        cells: [
          {
            size: 4,
            content: (
              <>
                <TextInputDisable
                  value={formatThousandDecimal(
                    formik.values.customerPaymentDifference,
                    getDecimalDigits(formik.values.customerPaymentDifference)
                  )}
                  className={classes.readOnlyNumber}
                  testId="customerPaymentDifference-text"
                />
                {isEditable &&
                  renderTextResult(
                    "error",
                    formik.errors?.customerPaymentDifference
                  )}
              </>
            )
          },
          currencyCell
        ]
      }
    },
    {
      key: "fee",
      config: {
        label: {
          size: 4,
          text: <FormattedMessage defaultMessage="ค่าธรรมเนียม" />
        },
        cells: [
          {
            size: 3,
            content: (
              <>
                {isEditable ? (
                  <NumberFormat
                    name="fee"
                    testId="fee-input"
                    value={`${formik.values.fee}`}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                    placeholder="0"
                    decimalScale={2}
                    fixedDecimalScale
                    rightAlignPlaceholder
                    customClass={classes.numberInput}
                    error={"fee" in formik.errors}
                  />
                ) : (
                  <TextInputDisable
                    value={formatThousandDecimal(
                      Number(formik.values.fee),
                      getDecimalDigits(Number(formik.values.fee))
                    )}
                    className={classes.readOnlyNumber}
                    testId="fee-text"
                  />
                )}
                {isEditable && renderTextResult("error", formik.errors?.fee)}
              </>
            )
          },
          {
            size: 2,
            content: isEditable ? (
              <SingleSelectField
                name="feeType"
                choices={feeTypeOptions}
                value={formik.values.feeType}
                onChange={handleChangeField}
                className={classes.selectField}
                InputProps={{
                  inputProps: { "data-testid": "feeType-input" }
                }}
              />
            ) : (
              <TextInputDisable
                value={FeeTypeLabel[formik.values.feeType]}
                className={classes.readOnlyText}
                testId="feeType-text"
              />
            )
          },
          {
            size: 3,
            content: (
              <Box className={classes.rowText} alignItems="center">
                <Checkbox
                  name="feeAbsorbFlag"
                  data-testid="feeAbsorbFlag-checkbox"
                  className={classes.checkbox}
                  size="small"
                  checked={formik.values.feeAbsorbFlag}
                  disabled={!isEditable}
                  onChange={handleChangeField}
                />
                <MyTypography variant="body3" style={{ marginBottom: 3 }}>
                  <FormattedMessage defaultMessage="SCGP Absorb" />
                </MyTypography>
              </Box>
            )
          }
        ]
      }
    },
    {
      key: "withHoldTax",
      config: {
        label: {
          size: 4,
          text: <FormattedMessage defaultMessage="ภาษีหัก ณ ที่จ่าย" />
        },
        cells: [
          {
            size: 3,
            content: (
              <>
                {isEditable ? (
                  <NumberFormat
                    name="withHoldTax"
                    testId="withHoldTax-input"
                    value={`${formik.values.withHoldTax}`}
                    onChange={handleChangeField}
                    onBlur={handleBlurField}
                    placeholder="0"
                    decimalScale={2}
                    fixedDecimalScale
                    rightAlignPlaceholder
                    customClass={classes.numberInput}
                    error={"withHoldTax" in formik.errors}
                  />
                ) : (
                  <TextInputDisable
                    value={formatThousandDecimal(
                      Number(formik.values.withHoldTax),
                      getDecimalDigits(Number(formik.values.withHoldTax))
                    )}
                    className={classes.readOnlyNumber}
                    testId="withHoldTax-text"
                  />
                )}
                {isEditable &&
                  renderTextResult("error", formik.errors?.withHoldTax)}
              </>
            )
          },
          { ...currencyCell, size: 5 }
        ]
      }
    },
    {
      key: "remark",
      config: {
        label: {
          size: 4,
          text: <FormattedMessage defaultMessage="หมายเหตุ" />
        },
        cells: [
          {
            size: 8,
            content: (
              <>
                {isEditable ? (
                  <CountTextField
                    name="remark"
                    data-testid="remark-input"
                    placeHolder=" "
                    maxLength={250}
                    rowsMin={1}
                    rowsMax={0}
                    value={formik.values.remark}
                    onBlur={handleChangeField}
                    className={classes.countTextField}
                  />
                ) : (
                  <TextInputDisable
                    value={formik.values.remark}
                    className={classes.readOnlyText}
                    testId="remark-text"
                  />
                )}
                {isEditable && renderTextResult("error", formik.errors?.remark)}
              </>
            )
          }
        ]
      }
    }
  ];

  return (
    <Grid
      container
      spacing={6}
      className={classes.root}
      data-testid="cashSaleForm"
      key={isEditable ? "edit" : "readonly"}
    >
      <Grid item container md={5} spacing={2} className={classes.detail}>
        {rows.map(r => renderFormRow(r.key, r.config))}
      </Grid>
      <Grid item md={7} className={classes.upload}>
        <DocumentUploader formik={formik} isEditable={isEditable} />
      </Grid>
    </Grid>
  );
};

export default CashSaleForm;
